import { HoverPopover } from '@app/components/base/HoverPopover';
import { PersonLane } from '@app/modules/persons/appearances/types';
import { Avatar, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { STREAM_UI_CONFIG } from '../../../../../consts';
import { HoverLaneSetter } from '../../shared/apply-lane-hover';
import { personsCastPopoverProps } from '../../shared/persons-track-popover-props';
import {
  APPEARANCE_BAR_DIMMED_OPACITY,
  CAST_AVATAR_PX,
} from '../../shared/persons-track-visual-tokens';
import { PersonCastAvatar } from './PersonCastAvatar';
import { PersonCastOverflowList } from './PersonCastOverflowList';

const MAX_VISIBLE = 3;
const AVATAR_PX = CAST_AVATAR_PX;
const STRIP_HEIGHT = STREAM_UI_CONFIG.personsSummaryHeight;

type PersonsCastClusterProps = {
  lanes: PersonLane[];
  hoveredLaneKey: string | null;
  onHoverLane: HoverLaneSetter;
};

/**
 * Cast rail on the left of the summary strip: who is in the focus window.
 *
 * Shows up to three overlapping avatars (person color border) plus a "+N"
 * badge when more people are present. Hovering an avatar highlights that
 * person's bars on the strip; "+N" opens a list of the rest. Clicks are
 * absorbed so they do not seek the track underneath.
 */
export function PersonsCastCluster({
  lanes,
  hoveredLaneKey,
  onHoverLane,
}: PersonsCastClusterProps) {
  const visible = lanes.slice(0, MAX_VISIBLE);
  const overflowLanes = lanes.slice(MAX_VISIBLE);
  const overflow = overflowLanes.length;
  const hasHover = !!hoveredLaneKey;
  const hoverInOverflow =
    !!hoveredLaneKey && overflowLanes.some((lane) => lane.laneKey === hoveredLaneKey);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 4,
        top: 0,
        // Full strip height so this is a solid hit target over pills underneath —
        // not just the avatar circles (which left gaps above/below).
        height: STRIP_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        zIndex: 4,
        px: 0.25,
        // Pill shape matching strip height; half-opaque so bars remain faintly visible.
        borderRadius: STRIP_HEIGHT / 2,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
        pointerEvents: 'auto',
      }}
      // Block seek / pill clicks that would otherwise land under the cast cluster.
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={() => onHoverLane(null)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          // Skip null avatars (not ready): overlap only real siblings.
          '& > *:not(:first-child)': { ml: -0.75 },
        }}
      >
        {visible.map((lane, index) => {
          const dimmed = hasHover && hoveredLaneKey !== lane.laneKey;
          const emphasized = hoveredLaneKey === lane.laneKey;
          return (
            <Box
              key={lane.laneKey}
              onMouseEnter={() => onHoverLane(lane.laneKey)}
              sx={{
                zIndex: emphasized ? visible.length + 1 : visible.length - index,
                display: 'inline-flex',
                opacity: dimmed ? APPEARANCE_BAR_DIMMED_OPACITY : 1,
                transition: 'opacity 120ms',
                // Collapse when PersonCastAvatar is null so first-child overlap works.
                '&:empty': { display: 'none' },
              }}
            >
              <PersonCastAvatar lane={lane} />
            </Box>
          );
        })}
        {overflow > 0 && (
          <HoverPopover
            trigger={
              <Avatar
                // Drop the previous visible-avatar highlight so overflow rows are not
                // all dimmed against a laneKey that is not in this list.
                onMouseEnter={() => onHoverLane(null)}
                sx={{
                  width: AVATAR_PX,
                  height: AVATAR_PX,
                  typography: 'caption',
                  zIndex: 0,
                  border: '1px solid',
                  borderColor: 'background.paper',
                  boxSizing: 'border-box',
                  // Dim +N when highlighting a visible cast avatar; keep it full when
                  // the hover target lives in this overflow list.
                  opacity: hasHover && !hoverInOverflow ? APPEARANCE_BAR_DIMMED_OPACITY : 1,
                  transition: 'opacity 120ms',
                }}
              >
                +{overflow}
              </Avatar>
            }
            {...personsCastPopoverProps}
          >
            <PersonCastOverflowList
              lanes={overflowLanes}
              hoveredLaneKey={hoveredLaneKey}
              onHoverLane={onHoverLane}
            />
          </HoverPopover>
        )}
      </Box>
    </Box>
  );
}
